import {
  CollectionCard,
  CollectionCardColumn,
  renderCollectionFieldSlot,
} from '@/components/blocks/collection/collection-card';
import {
  getCollectionCardPaddingClassName,
  getContentFields,
  getEnabledFields,
  getImageField,
  resolveSymmetricPadding,
} from '@/lib/blocks/collection/blocks';
import { isIndexPinnedCollectionBlock } from '@/lib/blocks/collection/index-collection/registry';
import { parseCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { readImageUrl } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { IndexCollectionCardLink } from './card-link';
import type { IndexCollectionCardComponentProps } from './index-collection-renderers';
import { resolveIndexCardConfig } from './utils';

export function BlogIndexCollectionCard({
  block,
  item,
  cardConfig,
  layout,
  renderField,
  variantProps,
}: IndexCollectionCardComponentProps) {
  const enabledFields = getEnabledFields(cardConfig.fields);
  const resolvedCardConfig = resolveIndexCardConfig(cardConfig, variantProps);
  const paddingClassName = getCollectionCardPaddingClassName(
    resolveSymmetricPadding(variantProps, 'none'),
  );
  const imageField = getImageField(enabledFields);
  const hasImage = Boolean(imageField && readImageUrl(item[imageField.slot]));
  const mediaLayout = parseCollectionMediaLayout(variantProps);
  const splitImageLeft = hasImage && mediaLayout.position === 'left';
  const pinnedBlock = isIndexPinnedCollectionBlock(block);
  const pinnedOverlay = pinnedBlock && layout === 'list';
  const useSplitLayout = (splitImageLeft || layout === 'list') && hasImage;
  const overlayHeightClass =
    layout === 'list' ? 'min-h-80 md:min-h-90' : 'min-h-96 md:min-h-[460px]';
  const useCardLinkFallback = !enabledFields.some((field) => field.kind === 'button');

  if (pinnedOverlay && hasImage && imageField) {
    return (
      <article className={cn('relative overflow-hidden rounded-lg', overlayHeightClass)}>
        <div className="absolute inset-0">{renderField(imageField, item[imageField.slot])}</div>
        <div className="absolute inset-0 bg-black/45" />
        <CollectionCardColumn
          gap={resolvedCardConfig.gap}
          align={resolvedCardConfig.align}
          paddingClassName={cn(
            'relative flex flex-col justify-end gap-4 p-6 text-white md:p-10',
            overlayHeightClass,
          )}
        >
          {getContentFields(enabledFields).map((field) =>
            renderCollectionFieldSlot(field, item, renderField),
          )}
        </CollectionCardColumn>
        <IndexCollectionCardLink item={item} enabled={useCardLinkFallback} />
      </article>
    );
  }

  return (
    <article
      className={cn(
        'relative h-full w-full',
        pinnedBlock && !pinnedOverlay && '@container @3xl:[&_img]:max-h-90',
        !useSplitLayout && 'flex flex-col',
        useSplitLayout && paddingClassName,
      )}
    >
      <CollectionCard
        item={item}
        cardConfig={resolvedCardConfig}
        renderField={renderField}
        mediaLayout={{ ...mediaLayout, position: useSplitLayout ? 'left' : 'top' }}
        paddingClassName={useSplitLayout ? undefined : paddingClassName}
      />
      <IndexCollectionCardLink item={item} enabled={useCardLinkFallback} />
    </article>
  );
}
